import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { ROUTES } from '@/utils/constants';
import { Button } from '@/components/ui/Button';
import { ThemeToggle } from '@/components/ui/ThemeToggle';
import {
  ShoppingCartIcon,
  UserIcon,
  Bars3Icon,
  XMarkIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  HeartIcon,
  SparklesIcon,
  ArrowPathIcon,
  HomeIcon,
  ShoppingBagIcon,
  InformationCircleIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import RoleSwitcherDropdown from '@/components/ui/RoleSwitcherDropdown';
import { MegaMenu, MegaMenuMobile } from '@/components/navigation/MegaMenu';
import { cn } from '@/utils/cn';

const Header: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);
  const [isCategoryMenuOpen, setIsCategoryMenuOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const categoryMenuRef = useRef<HTMLDivElement>(null);
  const moreMenuRef = useRef<HTMLDivElement>(null);

  const { isAuthenticated, user, logout, getDisplayName, getInitials, getDashboardRoute } = useAuth();
  const { totalItems = 0 } = useSelector((state: RootState) => state.cart);
  const navigate = useNavigate();
  const location = useLocation();

  // Handle scroll
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menus on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
      if (categoryMenuRef.current && !categoryMenuRef.current.contains(event.target as Node)) {
        setIsCategoryMenuOpen(false);
      }
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target as Node)) {
        setIsMoreMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  };

  const handleLogout = () => {
    logout();
    setIsUserMenuOpen(false);
    navigate('/');
  };

  const isActiveRoute = (route: string) => location.pathname === route;

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  return (
    <header
      className={cn(
        'sticky top-0 z-50 transition-all duration-300 bg-dark-900 dark:bg-dark-950',
        isScrolled ? 'shadow-lg' : 'shadow-sm'
      )}
    >
      {/* Single Layer Navbar */}
      <div className="max-w-[1800px] mx-auto px-3 sm:px-4 lg:px-6">
        <div className="flex items-center h-14 md:h-16 gap-1 md:gap-2 lg:gap-3">
          {/* Logo */}
          <Link
            to={ROUTES.HOME}
            className="flex items-center gap-2 flex-shrink-0 group"
          >
            <div className="w-8 h-8 md:w-10 md:h-10 relative flex items-center justify-center">
              <svg
                viewBox="0 0 48 48"
                className="w-full h-full"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <defs>
                  <linearGradient id="logoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#60a5fa" />
                    <stop offset="100%" stopColor="#3b82f6" />
                  </linearGradient>
                </defs>
                <rect x="10" y="18" width="28" height="26" rx="3" fill="url(#logoGradient)" />
                <rect x="8" y="12" width="32" height="8" rx="2" fill="url(#logoGradient)" />
                <path
                  d="M24 26l-4 6h8l-4-6zm-6 8l4-6-4-6-4 6 4 6zm12 0l-4-6 4-6 4 6-4 6z"
                  fill="#0d1117"
                  opacity="0.7"
                />
              </svg>
            </div>
            <div className="hidden sm:block">
              <span className="text-lg md:text-xl font-bold text-white tracking-tight">
                Recycler
              </span>
              <span className="text-lg md:text-xl font-bold text-primary-400">Bin</span>
            </div>
          </Link>

          {/* All Categories - Desktop */}
          <div ref={categoryMenuRef} className="relative hidden md:block">
            <button
              onClick={() => setIsCategoryMenuOpen(!isCategoryMenuOpen)}
              className={cn(
                'flex items-center gap-1.5 px-3 py-2 rounded-lg',
                'text-sm font-medium text-gray-200 hover:text-white hover:bg-dark-800',
                'transition-colors duration-200',
                isCategoryMenuOpen && 'bg-dark-800 text-white'
              )}
            >
              <Bars3Icon className="h-5 w-5" />
              <span className="hidden lg:inline">All Categories</span>
              <ChevronDownIcon className={cn(
                'h-4 w-4 transition-transform duration-200',
                isCategoryMenuOpen && 'rotate-180'
              )} />
            </button>
            <MegaMenu
              isOpen={isCategoryMenuOpen}
              onClose={() => setIsCategoryMenuOpen(false)}
              className="w-[90vw] max-w-6xl"
            />
          </div>

          {/* Search Bar - Desktop */}
          <form
            onSubmit={handleSearch}
            className="hidden md:flex flex-1 max-w-md lg:max-w-xl xl:max-w-2xl"
          >
            <div className="relative w-full">
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search products..."
                className={cn(
                  'w-full h-9 lg:h-10 pl-3 pr-10 rounded-lg text-sm',
                  'bg-dark-800 border border-dark-700',
                  'text-white placeholder-gray-400',
                  'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent',
                  'transition-all duration-200'
                )}
              />
              <button
                type="submit"
                className={cn(
                  'absolute right-0 top-0 h-full px-3',
                  'bg-primary-500 hover:bg-primary-600',
                  'text-white rounded-r-lg',
                  'transition-colors duration-200'
                )}
              >
                <MagnifyingGlassIcon className="h-4 w-4" />
              </button>
            </div>
          </form>

          {/* Navigation Links - Desktop (Large screens) */}
          <nav className="hidden xl:flex items-center gap-1">
            <Link
              to={ROUTES.PRODUCTS}
              className={cn(
                'px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                isActiveRoute(ROUTES.PRODUCTS)
                  ? 'text-primary-400 bg-dark-800'
                  : 'text-gray-300 hover:text-white hover:bg-dark-800'
              )}
            >
              All Products
            </Link>
            <Link
              to="/products?type=recycled"
              className="px-3 py-2 rounded-lg text-sm font-bold bg-green-600 text-white hover:bg-green-700 transition-colors flex items-center gap-1"
            >
              <ArrowPathIcon className="h-4 w-4" />
              Recycled
            </Link>
            <Link
              to="/products?featured=true"
              className="px-3 py-2 rounded-lg text-sm font-bold bg-amber-500 text-white hover:bg-amber-600 transition-colors flex items-center gap-1"
            >
              <SparklesIcon className="h-4 w-4" />
              Deals
            </Link>
          </nav>

          {/* More Dropdown - Desktop (for About, Contact + collapsed items on medium screens) */}
          <div ref={moreMenuRef} className="relative hidden md:block">
            <button
              onClick={() => setIsMoreMenuOpen(!isMoreMenuOpen)}
              className={cn(
                'flex items-center gap-1 px-3 py-2 rounded-lg',
                'text-sm font-medium text-gray-300 hover:text-white hover:bg-dark-800',
                'transition-colors duration-200',
                isMoreMenuOpen && 'bg-dark-800 text-white'
              )}
            >
              <span className="hidden lg:inline">More</span>
              <ChevronDownIcon className={cn(
                'h-4 w-4 transition-transform duration-200',
                isMoreMenuOpen && 'rotate-180'
              )} />
            </button>

            {isMoreMenuOpen && (
              <div className={cn(
                'absolute right-0 mt-2 w-48',
                'bg-white dark:bg-dark-800 rounded-xl',
                'shadow-lg border border-gray-200 dark:border-dark-700',
                'py-2 z-50',
                'animate-fade-in-down'
              )}>
                {/* Home - Always visible */}
                <Link
                  to={ROUTES.HOME}
                  className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700"
                  onClick={() => setIsMoreMenuOpen(false)}
                >
                  <HomeIcon className="h-4 w-4" />
                  Home
                </Link>
                {/* Show these on medium screens (hidden on xl) */}
                <div className="xl:hidden">
                  <Link
                    to={ROUTES.PRODUCTS}
                    className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700"
                    onClick={() => setIsMoreMenuOpen(false)}
                  >
                    <ShoppingBagIcon className="h-4 w-4" />
                    All Products
                  </Link>
                  <Link
                    to="/products?type=recycled"
                    className="flex items-center gap-3 px-4 py-2 text-sm font-medium text-green-600 dark:text-green-400 hover:bg-gray-50 dark:hover:bg-dark-700"
                    onClick={() => setIsMoreMenuOpen(false)}
                  >
                    <ArrowPathIcon className="h-4 w-4" />
                    Recycled
                  </Link>
                  <Link
                    to="/products?featured=true"
                    className="flex items-center gap-3 px-4 py-2 text-sm font-medium text-amber-600 dark:text-amber-400 hover:bg-gray-50 dark:hover:bg-dark-700"
                    onClick={() => setIsMoreMenuOpen(false)}
                  >
                    <SparklesIcon className="h-4 w-4" />
                    Deals
                  </Link>
                </div>
                <div className="border-t border-gray-100 dark:border-dark-700 my-1" />
                {/* Always visible */}
                <Link
                  to={ROUTES.ABOUT}
                  className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700"
                  onClick={() => setIsMoreMenuOpen(false)}
                >
                  <InformationCircleIcon className="h-4 w-4" />
                  About
                </Link>
                <Link
                  to={ROUTES.CONTACT}
                  className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700"
                  onClick={() => setIsMoreMenuOpen(false)}
                >
                  <PhoneIcon className="h-4 w-4" />
                  Contact
                </Link>
              </div>
            )}
          </div>

          {/* Spacer */}
          <div className="flex-1 md:flex-none" />

          {/* Right Side Actions */}
          <div className="flex items-center gap-1 md:gap-2">
            {/* Theme Toggle */}
            <ThemeToggle size="sm" className="text-gray-300 hover:text-white" />

            {/* Wishlist - Desktop */}
            {isAuthenticated && (
              <Link
                to={ROUTES.WISHLIST}
                className="hidden md:flex items-center p-2 text-gray-300 hover:text-white transition-colors rounded-lg hover:bg-dark-800"
                title="Wishlist"
              >
                <HeartIcon className="h-5 w-5" />
              </Link>
            )}

            {/* Cart */}
            <Link
              to={isAuthenticated ? ROUTES.CART : ROUTES.LOGIN}
              className="relative flex items-center p-2 text-gray-300 hover:text-white transition-colors rounded-lg hover:bg-dark-800"
              title="Cart"
            >
              <ShoppingCartIcon className="h-5 w-5" />
              {totalItems > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-primary-500 text-white text-xs font-bold rounded-full h-4 w-4 flex items-center justify-center text-[10px]">
                  {totalItems > 99 ? '99+' : totalItems}
                </span>
              )}
            </Link>

            {/* Role Switcher - Desktop */}
            {isAuthenticated && (
              <div className="hidden lg:block">
                <RoleSwitcherDropdown />
              </div>
            )}

            {/* User Menu */}
            {isAuthenticated ? (
              <div ref={userMenuRef} className="relative">
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className={cn(
                    'flex items-center gap-1.5 p-1.5 rounded-lg',
                    'text-gray-300 hover:text-white hover:bg-dark-800',
                    'transition-colors duration-200'
                  )}
                >
                  <div className="w-7 h-7 bg-primary-500/20 border border-primary-500/30 rounded-full flex items-center justify-center">
                    <span className="text-primary-400 font-medium text-xs">
                      {getInitials()}
                    </span>
                  </div>
                  <ChevronDownIcon className={cn(
                    'hidden sm:block h-4 w-4 transition-transform duration-200',
                    isUserMenuOpen && 'rotate-180'
                  )} />
                </button>

                {/* User Dropdown */}
                {isUserMenuOpen && (
                  <div className={cn(
                    'absolute right-0 mt-2 w-56',
                    'bg-white dark:bg-dark-800 rounded-xl',
                    'shadow-lg border border-gray-200 dark:border-dark-700',
                    'py-2 z-50',
                    'animate-fade-in-down'
                  )}>
                    <div className="px-4 py-2 border-b border-gray-100 dark:border-dark-700">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {getDisplayName()}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {user?.email}
                      </p>
                    </div>
                    <div className="py-1">
                      <Link
                        to={getDashboardRoute()}
                        className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <UserIcon className="h-4 w-4" />
                        Dashboard
                      </Link>
                      <Link
                        to={ROUTES.PROFILE}
                        className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <UserIcon className="h-4 w-4" />
                        Profile
                      </Link>
                      <Link
                        to={ROUTES.ORDERS}
                        className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <ShoppingCartIcon className="h-4 w-4" />
                        My Orders
                      </Link>
                      {/* Role Switcher in dropdown for medium screens */}
                      <div className="lg:hidden px-4 py-2 border-t border-gray-100 dark:border-dark-700">
                        <RoleSwitcherDropdown />
                      </div>
                    </div>
                    <div className="border-t border-gray-100 dark:border-dark-700 pt-1">
                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-3 w-full px-4 py-2 text-sm text-error-600 dark:text-error-400 hover:bg-gray-50 dark:hover:bg-dark-700"
                      >
                        Sign out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(ROUTES.LOGIN)}
                  className="text-gray-300 hover:text-white hover:bg-dark-800"
                >
                  Sign in
                </Button>
                <Button
                  variant="accent"
                  size="sm"
                  onClick={() => navigate(ROUTES.SIGNUP)}
                >
                  Sign up
                </Button>
              </div>
            )}

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 text-gray-300 hover:text-white transition-colors rounded-lg hover:bg-dark-800"
            >
              {isMobileMenuOpen ? (
                <XMarkIcon className="h-6 w-6" />
              ) : (
                <Bars3Icon className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Search Bar */}
      <div className="md:hidden bg-dark-800 dark:bg-dark-900 px-4 py-2 border-t border-dark-700">
        <form onSubmit={handleSearch}>
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search products..."
              className={cn(
                'w-full h-9 pl-3 pr-10 rounded-lg text-sm',
                'bg-dark-700 border border-dark-600',
                'text-white placeholder-gray-400',
                'focus:outline-none focus:ring-2 focus:ring-primary-500'
              )}
            />
            <button
              type="submit"
              className="absolute right-0 top-0 h-full px-3 bg-primary-500 hover:bg-primary-600 text-white rounded-r-lg"
            >
              <MagnifyingGlassIcon className="h-4 w-4" />
            </button>
          </div>
        </form>
      </div>

      {/* Mobile MegaMenu */}
      <MegaMenuMobile
        isOpen={isMobileMenuOpen && isCategoryMenuOpen}
        onClose={() => {
          setIsCategoryMenuOpen(false);
          setIsMobileMenuOpen(false);
        }}
      />

      {/* Mobile Menu */}
      {isMobileMenuOpen && !isCategoryMenuOpen && (
        <div className="md:hidden bg-white dark:bg-dark-800 border-t border-gray-200 dark:border-dark-700 max-h-[70vh] overflow-y-auto">
          <div className="px-4 py-3 space-y-1">
            {/* Categories Button */}
            <button
              onClick={() => setIsCategoryMenuOpen(true)}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
            >
              <Bars3Icon className="h-5 w-5" />
              Browse Categories
              <ChevronDownIcon className="h-4 w-4 ml-auto" />
            </button>

            <div className="border-t border-gray-200 dark:border-dark-700 my-2" />

            {/* Main Navigation */}
            <Link
              to={ROUTES.HOME}
              className={cn(
                'flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-colors',
                isActiveRoute(ROUTES.HOME)
                  ? 'text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700'
              )}
              onClick={closeMobileMenu}
            >
              <HomeIcon className="h-5 w-5" />
              Home
            </Link>
            <Link
              to={ROUTES.PRODUCTS}
              className={cn(
                'flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-colors',
                isActiveRoute(ROUTES.PRODUCTS)
                  ? 'text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700'
              )}
              onClick={closeMobileMenu}
            >
              <ShoppingBagIcon className="h-5 w-5" />
              All Products
            </Link>
            <Link
              to="/products?type=recycled"
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-base font-bold bg-green-600 text-white hover:bg-green-700 transition-colors"
              onClick={closeMobileMenu}
            >
              <ArrowPathIcon className="h-5 w-5" />
              Recycled Items
            </Link>
            <Link
              to="/products?featured=true"
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-base font-bold bg-amber-500 text-white hover:bg-amber-600 transition-colors"
              onClick={closeMobileMenu}
            >
              <SparklesIcon className="h-5 w-5" />
              Deals
            </Link>

            <div className="border-t border-gray-200 dark:border-dark-700 my-2" />

            <Link
              to={ROUTES.ABOUT}
              className={cn(
                'flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-colors',
                isActiveRoute(ROUTES.ABOUT)
                  ? 'text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700'
              )}
              onClick={closeMobileMenu}
            >
              <InformationCircleIcon className="h-5 w-5" />
              About
            </Link>
            <Link
              to={ROUTES.CONTACT}
              className={cn(
                'flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-colors',
                isActiveRoute(ROUTES.CONTACT)
                  ? 'text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700'
              )}
              onClick={closeMobileMenu}
            >
              <PhoneIcon className="h-5 w-5" />
              Contact
            </Link>

            {isAuthenticated && (
              <>
                <div className="border-t border-gray-200 dark:border-dark-700 my-2" />
                <Link
                  to={getDashboardRoute()}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700"
                  onClick={closeMobileMenu}
                >
                  <UserIcon className="h-5 w-5" />
                  Dashboard
                </Link>
                <Link
                  to={ROUTES.WISHLIST}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700"
                  onClick={closeMobileMenu}
                >
                  <HeartIcon className="h-5 w-5" />
                  Wishlist
                </Link>
                <Link
                  to={ROUTES.ORDERS}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700"
                  onClick={closeMobileMenu}
                >
                  <ShoppingCartIcon className="h-5 w-5" />
                  My Orders
                </Link>
                <div className="px-4 py-2">
                  <RoleSwitcherDropdown />
                </div>
              </>
            )}

            {!isAuthenticated && (
              <div className="pt-4 space-y-2">
                <Button
                  variant="outline"
                  fullWidth
                  onClick={() => {
                    navigate(ROUTES.LOGIN);
                    closeMobileMenu();
                  }}
                >
                  Sign in
                </Button>
                <Button
                  variant="accent"
                  fullWidth
                  onClick={() => {
                    navigate(ROUTES.SIGNUP);
                    closeMobileMenu();
                  }}
                >
                  Create Account
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
